import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  Sparkles,
  Loader2,
  ShieldCheck,
  UploadCloud,
  ArrowRight,
  MessageSquare,
  FileText,
  Lock,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import UploadModal from "../components/UploadModal";
import AuthModal from "../components/AuthModal";
import SEO from "../components/SEO";
import { useAuth } from "../context/AuthContext";
import { listSessionUploads } from "../lib/uploads";
import { listMyUploads } from "../lib/authApi";

function relativeTime(iso) {
  const d = new Date(iso);
  const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} h ago`;
  const days = Math.floor(diffSec / 86400);
  return `${days} d ago`;
}

export default function AskAI() {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [anonUploads, setAnonUploads] = useState([]);
  const [myUploads, setMyUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirectTo = params.get("redirect");

  const loadAnon = useCallback(async () => {
    try {
      const data = await listSessionUploads();
      setAnonUploads(data.filter((u) => u.analysis_status === "complete"));
    } catch (e) {
      setAnonUploads([]);
    }
  }, []);

  const loadMine = useCallback(async () => {
    try {
      const data = await listMyUploads();
      setMyUploads(data.filter((u) => u.analysis_status === "complete"));
    } catch (e) {
      setMyUploads([]);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    (async () => {
      setLoading(true);
      if (user) {
        await loadMine();
      } else {
        await loadAnon();
      }
      setLoading(false);
    })();
  }, [authLoading, user, loadMine, loadAnon]);

  // Authed with policies → jump straight to the latest chat OR the redirect target.
  useEffect(() => {
    if (!user || loading) return;
    if (redirectTo) {
      navigate(redirectTo, { replace: true });
      return;
    }
    if (myUploads.length === 1) {
      navigate(`/chat/${myUploads[0].id}`, { replace: true });
    }
  }, [user, loading, myUploads, redirectTo, navigate]);

  const onAuthSuccess = async () => {
    setAuthOpen(false);
    await loadMine();
    // Prefer the redirect target if set, otherwise the freshest policy.
    if (redirectTo) {
      navigate(redirectTo, { replace: true });
      return;
    }
    // Wait a tick for state update, then pick the top policy.
    setTimeout(async () => {
      const data = await listMyUploads();
      const ready = data.filter((u) => u.analysis_status === "complete");
      if (ready[0]) navigate(`/chat/${ready[0].id}`, { replace: true });
    }, 50);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO
        path="/ask"
        title="Ask AI — chat with your motor policy"
        description="Ask questions about your uploaded motor insurance policy and get answers grounded strictly in the actual policy text. Free, private, and never gives advice — just what your document says."
      />
      <Navbar onUploadClick={() => setUploadOpen(true)} />

      <div
        className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 pt-10 md:pt-14 pb-16"
        data-testid="ask-ai-landing"
      >
        <div className="text-center">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-blue-600 text-white flex items-center justify-center shadow-lg shadow-blue-600/20">
            <MessageSquare className="h-6 w-6" />
          </div>
          <h1
            className="mt-6 font-display text-3xl md:text-4xl tracking-tight text-slate-900"
            data-testid="ask-title"
          >
            Ask Your Policy
          </h1>
          <p className="mt-3 text-slate-600 max-w-md mx-auto">
            Talk to an AI grounded strictly in your uploaded policy text. Never
            an opinion — always what the document actually says.
          </p>
        </div>

        {/* Body */}
        {authLoading || loading ? (
          <div
            className="mt-10 flex items-center justify-center text-slate-500 text-sm"
            data-testid="ask-loading"
          >
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Loading…
          </div>
        ) : !user ? (
          anonUploads.length === 0 ? (
            <div
              className="mt-10 border-2 border-dashed border-slate-300 rounded-2xl p-10 text-center bg-slate-50"
              data-testid="ask-no-upload"
            >
              <div className="h-12 w-12 rounded-full bg-white border border-slate-200 flex items-center justify-center text-blue-600 shadow-sm mx-auto">
                <UploadCloud className="h-5 w-5" />
              </div>
              <h2 className="mt-4 font-display text-lg font-semibold text-slate-900">
                Upload a policy first
              </h2>
              <p className="mt-1 text-sm text-slate-500 max-w-sm mx-auto">
                Upload your motor insurance PDF and wait for the analysis to
                finish — then you can chat with it.
              </p>
              <button
                onClick={() => setUploadOpen(true)}
                data-testid="ask-upload-btn"
                className="mt-5 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-5 py-2.5 text-sm font-medium"
              >
                <UploadCloud className="h-4 w-4" />
                Upload PDF
              </button>
            </div>
          ) : (
            <div
              className="mt-10 border border-slate-200 rounded-2xl p-6 md:p-8 bg-white shadow-sm"
              data-testid="ask-login-gate"
            >
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center shrink-0">
                  <Lock className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="font-display text-lg font-semibold text-slate-900">
                    Log in to chat with your policy
                  </h2>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                    Uploading and viewing your analysis stay open. Login only
                    unlocks the chat — and links your uploaded policy to your
                    account.
                  </p>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <div className="text-xs uppercase tracking-widest text-slate-400 font-medium">
                  Ready to chat about
                </div>
                {anonUploads.slice(0, 3).map((u) => (
                  <div
                    key={u.id}
                    className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200"
                    data-testid={`anon-upload-${u.id}`}
                  >
                    <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                      <FileText className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {u.insurer && u.insurer !== "not specified in your policy"
                          ? `${u.insurer}${u.vehicle && u.vehicle !== "not specified in your policy" ? " — " + u.vehicle : ""}`
                          : u.filename}
                      </div>
                      <div className="text-xs text-slate-500">
                        Uploaded {relativeTime(u.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => setAuthOpen(true)}
                data-testid="ask-login-btn"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full py-3 text-sm font-medium shadow-sm"
              >
                <Sparkles className="h-4 w-4" />
                Sign up or log in to continue
                <ArrowRight className="h-4 w-4" />
              </button>

              <p className="mt-3 text-[11px] text-slate-400 text-center">
                We use your email only for account access.
              </p>
            </div>
          )
        ) : myUploads.length === 0 ? (
          <div
            className="mt-10 border-2 border-dashed border-slate-300 rounded-2xl p-10 text-center bg-slate-50"
            data-testid="ask-authed-empty"
          >
            <div className="h-12 w-12 rounded-full bg-white border border-slate-200 flex items-center justify-center text-blue-600 shadow-sm mx-auto">
              <UploadCloud className="h-5 w-5" />
            </div>
            <h2 className="mt-4 font-display text-lg font-semibold text-slate-900">
              No policies linked to your account yet
            </h2>
            <p className="mt-1 text-sm text-slate-500 max-w-sm mx-auto">
              Upload a PDF and it&apos;ll be attached to your account for chat.
            </p>
            <button
              onClick={() => setUploadOpen(true)}
              data-testid="ask-authed-upload"
              className="mt-5 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-5 py-2.5 text-sm font-medium"
            >
              <UploadCloud className="h-4 w-4" />
              Upload PDF
            </button>
          </div>
        ) : (
          <div className="mt-10 space-y-3" data-testid="ask-policy-picker">
            <div className="text-xs uppercase tracking-widest text-slate-400 font-medium mb-2">
              Pick a policy to chat about
            </div>
            {myUploads.map((u) => (
              <Link
                key={u.id}
                to={`/chat/${u.id}`}
                data-testid={`chat-picker-${u.id}`}
                className="flex items-center gap-4 p-4 border border-slate-200 rounded-2xl hover:border-blue-300 hover:shadow-sm bg-white"
              >
                <div className="h-11 w-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                  <FileText className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-slate-900 truncate">
                    {u.insurer && u.insurer !== "not specified in your policy"
                      ? `${u.insurer}${u.vehicle && u.vehicle !== "not specified in your policy" ? " — " + u.vehicle : ""}`
                      : u.filename}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {u.filename} · Uploaded {relativeTime(u.created_at)}
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400" />
              </Link>
            ))}
          </div>
        )}

        <div className="mt-14 flex items-start gap-3 text-xs text-slate-500 border-t border-slate-200 pt-6">
          <ShieldCheck className="h-4 w-4 mt-0.5 text-slate-400" />
          <p className="leading-relaxed">
            The chat is grounded in your uploaded PDF only. If something
            isn&apos;t in your policy, the assistant will tell you so — never
            guess. It explains — it never advises.
          </p>
        </div>
      </div>

      <Footer />
      <UploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={() => (user ? loadMine() : loadAnon())}
      />
      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        onSuccess={onAuthSuccess}
        headline="One step to chat with your policy"
        subheadline="Free forever. We'll link the PDF you just uploaded to your account."
      />
    </div>
  );
}
